/**
 * Firebase Storage Service
 * Handles file uploads and deletions
 */

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage } from '../firebase'

const UPLOAD_PATH = 'temp-uploads'

/**
 * Get or create upload session ID
 * Stored in localStorage for consistency across uploads
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem('upload_session_id')
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    localStorage.setItem('upload_session_id', sessionId)
  }
  return sessionId
}

/**
 * Upload file to Firebase Storage
 *
 * @param {File} file - File object to upload
 * @param {string} type - Upload type ('logo', 'page', 'additional')
 * @param {string} id - Optional ID (e.g., pageId)
 * @returns {Promise<Object>} File metadata with URL
 */
export const uploadFile = async (file, type, id = null) => {
  try {
    // Validate file size (10MB limit)
    const MAX_SIZE = 10 * 1024 * 1024 // 10MB
    if (file.size > MAX_SIZE) {
      throw new Error('File size exceeds 10MB limit')
    }

    const sessionId = getSessionId()
    const timestamp = Date.now()
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
    const filename = `${type}${id ? `-${id}` : ''}-${timestamp}-${sanitizedName}`
    const filePath = `${UPLOAD_PATH}/${sessionId}/${filename}`

    // Create storage reference
    const storageRef = ref(storage, filePath)

    // Upload file
    const snapshot = await uploadBytes(storageRef, file)

    // Get public download URL
    const url = await getDownloadURL(snapshot.ref)

    // Return file metadata
    return {
      name: file.name,
      size: file.size,
      url: url,
      uploadedAt: new Date().toISOString(),
      storagePath: filePath,
      type: file.type
    }
  } catch (error) {
    console.error('Upload error:', error)
    throw error
  }
}

/**
 * Delete file from Firebase Storage
 *
 * @param {string} storagePath - Full storage path of file
 * @returns {Promise<void>}
 */
export const deleteFile = async (storagePath) => {
  try {
    const storageRef = ref(storage, storagePath)
    await deleteObject(storageRef)
    console.log('File deleted:', storagePath)
  } catch (error) {
    console.error('Delete error:', error)
    throw error
  }
}

/**
 * Format file size for display
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}
